import type {
  GraphQL,
  HotelBookableOptionRoom,
  HotelBookableOptionWithRoomsAndDateAndPrices,
  RoomPax,
  SmartSearchImageUrl,
} from '@libs/trip-planner-interfaces';
import { isFeatureToggleActive } from '@libs/feature-toggle';
import { isDefined } from '@libs/utils';

import { assertSmartSearchList, assertSmartSearchValue } from '../../utils';

import { toMoney } from './moneyResponseQL';
import {
  toHotelBookableOption,
  toHotelBookableRoom,
} from './hotelBookableOptionResponseQL';

export function toProductAvailabilityResponseQL(
  productAvailabilityResponseQL: GraphQL.searchProductAvailabilitySubscription
): HotelBookableOptionWithRoomsAndDateAndPrices {
  assertSmartSearchValue(productAvailabilityResponseQL.searchCheapestStay);
  const productAvailabilityResponse =
    productAvailabilityResponseQL.searchCheapestStay;

  assertSmartSearchList(productAvailabilityResponse.rooms);
  assertSmartSearchValue(productAvailabilityResponse.totalPrice);
  assertSmartSearchValue(productAvailabilityResponse.prices);
  assertSmartSearchValue(productAvailabilityResponse.prices.totalPrice);

  return {
    ...toHotelBookableOption(productAvailabilityResponse),
    date: new Date(productAvailabilityResponse.date),
    rooms: productAvailabilityResponse.rooms.map(toHotelBookableOptionRoom),
    prices: {
      totalPrice: toMoney(productAvailabilityResponse.prices.totalPrice),
      grossPrice: productAvailabilityResponse.prices.grossPrice
        ? toMoney(productAvailabilityResponse.prices.grossPrice)
        : undefined,
    },
  };
}

export type ProductAvailabilityResponseQL = NonNullable<
  GraphQL.searchProductAvailabilitySubscription['searchCheapestStay']
>;
type RoomsQL = NonNullable<ProductAvailabilityResponseQL['rooms']>;
type RoomQL = NonNullable<RoomsQL[number]>;

function toHotelBookableOptionRoom(roomQL: RoomQL): HotelBookableOptionRoom {
  assertSmartSearchList(roomQL.pax);

  const childAges = roomQL.pax
    .map((p) => p.age)
    .filter<number>((age): age is number => age !== null);

  const numberOfAdults = roomQL.pax.length - childAges.length;

  const pax: RoomPax = {
    numberOfAdults,
    childAges,
  };

  assertSmartSearchList(roomQL.images);

  return {
    ...toHotelBookableRoom(roomQL),
    pax,
    imageUrls: roomQL.images
      .map((image) => image.imageUrl)
      .filter((url): url is string => typeof url === 'string')
      .map((url) => url as SmartSearchImageUrl),
    roomCode: roomQL.roomCode,
    roomTypeId:
      isDefined(roomQL.roomTypeId) && isFeatureToggleActive('room-types')
        ? roomQL.roomTypeId
        : undefined,
  };
}
